import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * The insurance verdict (src/model/eligibility) for a batch of visits or patients — what the web
 * console reads instead of judging eligibility responses and clinic decisions itself, so it shows
 * the same verdict as the provider app, Billing V2 and BeAssistant.
 *
 * A subject is a visit (`appointmentId`: the Coverage attached to it, none for a self-pay or
 * uninsured visit) or a patient (`patientId`: the given `coverageId`, else their primary
 * Coverage). The clinic decision is the caller's organization's. Value sets are inlined from
 * src/model/eligibility/types.ts — contract files stay browser-safe.
 */
export const INSURANCE_VERDICTS_MAX_SUBJECTS = 500

const subjectSchema = z.union([
  z.strictObject({ appointmentId: z.string().min(1) }),
  z.strictObject({ patientId: z.string().min(1), coverageId: z.string().min(1).optional() }),
])

const verdictSchema = z.object({
  payerEligibility: z.enum([
    'verified',
    'approved-manually',
    'disapproved',
    'not-verified',
    'none',
  ]),
  clinicDecision: z.enum(['approve', 'disapprove']).nullable(),
  insuranceStatus: z.enum(['verified', 'review-required', 'none']),
})

export const insuranceVerdicts = oc
  .route({ method: 'POST', path: '/v2/insurance/verdicts' })
  .input(z.object({ subjects: z.array(subjectSchema).min(1).max(INSURANCE_VERDICTS_MAX_SUBJECTS) }))
  // One verdict per subject, in input order.
  .output(z.object({ verdicts: z.array(verdictSchema) }))
